import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { accounts as accountsApi } from "../api/endpoints";
import type { Account } from "../api/types";
import { AccountPicker } from "../components/AccountPicker";
import { ConnectionPill } from "../components/ConnectionPill";
import { RiskCard } from "../components/RiskCard";
import { SleeveSizingCard } from "../components/SleeveSizingCard";

// Set-and-forget config, split off the live Desk so the trading view
// stays scannable. Risk knobs (max contracts / daily loss cap / NLV
// floor) and per-sleeve contract sizing live here — both apply
// immediately on save and are touched rarely, not glanced at on a hot
// cadence. No live polling cadence here; a 10s account refetch is plenty.
export function ConfigPage() {
  const accountsQ = useQuery({
    queryKey: ["accounts"],
    queryFn: accountsApi.list,
    refetchInterval: 10_000,
  });

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const list = accountsQ.data ?? [];
  const account: Account | undefined = useMemo(() => {
    if (selectedId !== null) return list.find((a) => a.id === selectedId);
    return list[0];
  }, [list, selectedId]);

  if (accountsQ.isLoading) {
    return <div className="muted">loading accounts…</div>;
  }
  if (list.length === 0) {
    return (
      <div className="card">
        <h2>No account yet</h2>
        <p>
          You haven't created a cash account.{" "}
          <Link to="/setup">Run the setup flow</Link> to create one first.
        </p>
      </div>
    );
  }
  if (!account) {
    return <div className="muted">account not found</div>;
  }

  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }}>
      <div className="row wrap" style={{ marginBottom: 8, gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 13 }}>{account.name}</h3>
        <ConnectionPill account={account} />
        <span className="muted small">
          #{account.id} · {account.broker}
        </span>
        <div className="spacer" />
        <AccountPicker accounts={list} selectedId={account.id} onSelect={setSelectedId} />
      </div>

      <RiskCard account={account} />

      <SleeveSizingCard account={account} />
    </div>
  );
}